import React, { useState } from 'react';
import styled from 'styled-components';
import { styled as MUIStyled } from '@mui/system';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import IosShareIcon from '@mui/icons-material/IosShare';
import MenuIcon from '@mui/icons-material/Menu';
import TooltipBase, { tooltipClasses } from '@mui/material/Tooltip';
import { ClickAwayListener } from '@mui/base';
import { usePlayground } from '../../shared/context/PlaygroundContext';
import ShareModal from './ShareModal';

// Styled Components - matching original Playground.tsx styles
const NavBar = styled.div<{ $isMobile: boolean; $sidebarWidth: number }>`
  position: fixed;
  top: 2px;
  left: ${({ $isMobile, $sidebarWidth }) =>
    $isMobile ? '15px' : `${$sidebarWidth}px`};
  width: ${({ $isMobile, $sidebarWidth }) =>
    $isMobile ? '100vw' : `calc(100vw - ${$sidebarWidth}px)`};
  height: 40px;
  padding: 0 10px 0 20px;
  z-index: 1150;
  background-color: transparent;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: left 0.18s ease-out, width 0.18s ease-out;
`;

const NavBarItem = styled.div`
  position: relative;
  cursor: pointer;
  display: flex;
  font-weight: 500;
  font-size: 0.95rem;
  line-height: 1.25rem;
  font-family: 'Noka', sans-serif;
  color: ${({theme})=> theme.font_color};
  padding: 0.35rem 0.6rem;
  border-radius: 0.5rem;
  gap: 0.25rem;
  align-items: center;
  transition: color 0.2s ease, background-color 0.2s ease;
  &:hover {
    background-color: ${({ theme }) => theme.sidebar_cta_border};
    color: ${({ theme }) => theme.font_color};
  }
`;

const DrodownMenu = styled.div`
  position: absolute;
  z-index: 1200;
  top: calc(100% + 2px);
  left: 50%;
  transform: translateX(-50%);
  border-radius: 10px;
  border: 1px solid ${({theme})=> theme.user_menu_border};
  background-color: ${({theme})=> theme.sidebar_background_solid};
  min-width: 20rem;
  max-width: 30rem;
  max-height: 70vh;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const DropdownMenuItem = styled.div`
  display: flex;
  cursor: pointer;
  line-height: 1.25rem;
  font-family: 'Noka', sans-serif;
  padding: 12px 16px;
  border-radius: 0;
  gap: 0.5rem;
  margin: 0;
  color: ${({theme})=> theme.font_color};
  transition: background-color 0.15s ease;

  &:hover {
    background: ${({theme})=> theme.hover_overlay_light};
  }
`;

const DropdownMenuItemText = styled.div`
  flex-grow: 1;
  text-align: left;
  display: flex;
  flex-direction: column;
  margin: 0;
`;

const DropdownMenuItemDescription = styled.div`
  display: flex;
  color: ${({theme})=> theme.menu_item_description_color};
  max-width: 18rem;
  font-size: 0.875rem;
`;

const TabContent = styled.div`
  max-height: calc(70vh - 50px);
  overflow-y: auto;
`;

const MuiIConDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MuiIConUnselectedModel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ExpandDropdown = MUIStyled(ExpandMoreIcon)` font-size: 1rem; `;
const SelectedModelIcon = MUIStyled(CheckCircleRoundedIcon)` font-size: 1.35rem; `;
const UnselectedModelIcon = MUIStyled(RadioButtonUncheckedRoundedIcon)` font-size: 1.35rem; `;

const StylizedMenuIcon = styled(MenuIcon)`
  position: fixed;
  top: 25px;
  left: 15px;
  z-index: 1200;
  display: inline-block;
  color: ${({ theme }) => theme.font_color};
  font-size: 1.7rem;
  cursor: pointer;
`;

const ShareTooltip = styled(({ className, ...props }: any) => (
  <TooltipBase {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    fontSize: '0.85rem',
  },
});

const ShareButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 6px;
  border-radius: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.action_icon_color || theme.font_color};
  transition: background-color 0.15s ease, color 0.15s ease;
  &:hover {
    background-color: ${({ theme }) => theme.sidebar_cta_border};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ShareIconStyled = MUIStyled(IosShareIcon)`
  font-size: 1.2rem;
`;

const ChatHeader: React.FC = () => {
  const {
    isMobile,
    sidebarWidth,
    modelChat,
    openDropdownMenu,
    openSidebar,
    modelDescriptions,
    dropdownMenuOptions,
    activeChatID,
    messageHistory,
    setOpenDropdownMenu,
    handleSelectOption,
    handleSidebarToggle,
  } = usePlayground();

  const [shareModalOpen, setShareModalOpen] = useState(false);

  const handleClickAwayDropdownMenu = () => {
    if (openDropdownMenu) {
      setOpenDropdownMenu(false);
    }
  };

  const hasMessages = messageHistory && messageHistory.length > 0;
  const hasChatId = Boolean(activeChatID?.chatID);

  return (
    <>
      <NavBar $isMobile={isMobile} $sidebarWidth={sidebarWidth}>
        <HeaderLeftSection />

        {hasChatId && hasMessages && (
          <HeaderRightSection>
            <ShareTooltip
              title="Compartilhar conversa"
              placement="bottom"
            >
              <ShareButton
                onClick={() => setShareModalOpen(true)}
                aria-label="Compartilhar conversa"
              >
                <ShareIconStyled />
              </ShareButton>
            </ShareTooltip>
          </HeaderRightSection>
        )}
      </NavBar>

      {!openSidebar && isMobile && (
        <StylizedMenuIcon onClick={() => handleSidebarToggle(true)} />
      )}

      <ShareModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        chatId={activeChatID?.chatID || ''}
      />
    </>
  );
};

export default ChatHeader;

// Additional styled components
const HeaderLeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderRightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 8px;
`;
